import { CheckCircle, Crown, Mail, Music, Timer, History } from 'lucide-react';

const CONFETTI_COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4',
];

const PIECES = Array.from({ length: 60 }, (_, i) => ({
  left: (i * 61) % 100,
  delay: (i % 10) * 0.35,
  duration: 2.8 + ((i * 7) % 20) / 10,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  size: 6 + ((i * 13) % 8),
  drift: ((i * 17) % 40) - 20,
}));

const BENEFITS = [
  { icon: Timer, text: 'Unlimited focus sessions and cycles' },
  { icon: Music, text: 'Your own custom focus and break sounds' },
  { icon: History, text: 'Full session history, kept forever' },
];

interface PremiumSuccessDialogProps {
  onDone: () => void;
}

/**
 * Celebration shown when a payment is confirmed. Pure CSS confetti — no
 * animation dependency.
 */
export function PremiumSuccessDialog({ onDone }: PremiumSuccessDialogProps) {
  return (
    <div className="fixed inset-0 z-[60] overflow-hidden bg-black/60 flex items-center justify-center p-4">
      {/* Confetti */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {PIECES.map((piece, i) => (
          <span
            key={i}
            className="absolute rounded-sm"
            style={{
              left: `${piece.left}%`,
              top: '-3%',
              width: piece.size,
              height: piece.size * 0.6,
              backgroundColor: piece.color,
              animation: `pomo-confetti-fall ${piece.duration}s linear ${piece.delay}s infinite`,
              // Horizontal drift via a CSS variable consumed by the keyframes.
              ['--pomo-drift' as string]: `${piece.drift}px`,
            }}
          />
        ))}
      </div>

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-gradient-to-r from-amber-400 to-amber-600 text-white p-4 rounded-full shadow-lg">
            <Crown className="w-10 h-10" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          You're Premium!
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Payment confirmed — everything is unlocked.
        </p>

        <ul className="space-y-3 text-left mb-6">
          {BENEFITS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <Icon className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
              <span>{text}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg p-3 mb-6 text-left">
          <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-indigo-500" />
          <span>
            Paid as a guest? No problem — you can log in any time with your
            email and your premium follows you to any device.
          </span>
        </div>

        <button
          onClick={onDone}
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
        >
          Start focusing
        </button>
      </div>
    </div>
  );
}
